import { Link, usePage } from "@inertiajs/react"
import { AlertCircle, ArrowRight } from "lucide-react"
import type { ReactNode } from "react"

import { AdminLayout } from "@/layouts/AdminLayout"
import type { NeedsAttentionItem, NeedsAttentionSignal, RegionalKPI } from "@/lib/types"
import type { SharedProps } from "@/types"

interface TopRegion {
  id: number
  name: string
  sessions_30d: number
}

interface PlatformKPIs {
  properties_total: number
  properties_live: number
  properties_suspended: number
  pending_moderation: number
  active_guides: number
  guest_sessions_7d: number
  guest_sessions_30d: number
  channel_wifi: number
  channel_qr: number
  top_regions: TopRegion[]
}

interface Props {
  platform_kpis: PlatformKPIs | null
  regional_kpis: RegionalKPI | null
  needs_attention: NeedsAttentionItem[]
}

const SIGNAL_LABELS: Record<NeedsAttentionSignal, string> = {
  no_default_guide:            "No default guide",
  no_wifi:                     "No WiFi config",
  few_pois:                    "Fewer than 5 POIs",
  no_inside_info:              "No INSIDE info",
  stuck_onboarding:            "Stuck in onboarding >7 days",
  no_active_delivery_channel:  "No active delivery channel",
  delivery_channel_inactive:   "No guest scans in 30 days",
}

function KpiCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border bg-card px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  )
}

function NeedsAttentionBlock({ items }: { items: NeedsAttentionItem[] }) {
  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">All properties on track.</p>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {items.map((item) => (
        <div
          key={`${item.property_id}-${item.signal}`}
          className="flex items-center justify-between rounded-lg border bg-card px-4 py-3"
        >
          <div className="flex items-center gap-3">
            <AlertCircle aria-hidden="true" className="size-4 text-yellow-500 shrink-0" />
            <div>
              <Link
                href={item.url}
                className="text-sm font-medium hover:underline"
              >
                {item.property_name}
              </Link>
              <p className="text-xs text-muted-foreground">{SIGNAL_LABELS[item.signal]}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function ChannelSplitBar({ wifi, qr }: { wifi: number; qr: number }) {
  const total = wifi + qr
  if (total === 0) return <p className="text-xs text-muted-foreground">No active shares</p>
  const wifiPct = Math.round((wifi / total) * 100)
  return (
    <div className="mt-2 flex flex-col gap-1">
      <div className="flex h-2 overflow-hidden rounded-full bg-muted">
        <div className="bg-primary" style={{ width: `${wifiPct}%` }} />
        <div className="flex-1 bg-secondary" />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>WiFi {wifiPct}%</span>
        <span>QR {100 - wifiPct}%</span>
      </div>
    </div>
  )
}

export default function Dashboard({ platform_kpis, regional_kpis, needs_attention }: Props) {
  const { auth, user_context, accessible_properties } = usePage<SharedProps>().props

  const user = auth?.user
  const displayName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email
    : "there"

  const ctx = user_context

  const sections = [
    {
      label:     "Platform",
      available: ctx.is_admin || ctx.is_support,
      description: "Regions, distributors, properties, moderation",
    },
    {
      label:     "Distribution",
      available: ctx.managed_region_ids.length > 0,
      description: ctx.managed_regions.length > 0
        ? ctx.managed_regions.map((r) => r.name).join(", ")
        : `${ctx.managed_region_ids.length} managed region(s)`,
    },
    {
      label:     "Property",
      available: accessible_properties.length > 0,
      description: `${accessible_properties.length} accessible propert${accessible_properties.length === 1 ? "y" : "ies"}`,
    },
  ]

  const hasRegionalContext = regional_kpis !== null
  const showPlatform = ctx.is_admin || ctx.is_support
  const showDistribution = ctx.managed_region_ids.length > 0
  const isPropertyOnlyOwner = !showPlatform && !showDistribution && accessible_properties.length > 0

  return (
    <div className="mx-auto max-w-3xl flex flex-col gap-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Welcome to INNLY Admin, {displayName}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Here&rsquo;s what you have access to.
        </p>

        {isPropertyOnlyOwner && (
          <Link
            href={accessible_properties[0].admin_url}
            className="mt-6 flex items-center justify-between rounded-xl border bg-card px-4 py-3 hover:bg-accent transition-colors"
          >
            <div>
              <p className="text-sm font-medium">{accessible_properties[0].name}</p>
              <p className="text-xs text-muted-foreground">Go to your property</p>
            </div>
            <ArrowRight className="size-4 text-muted-foreground" />
          </Link>
        )}

        <div className="mt-6 grid gap-3">
          {sections.map((section) => (
            <div
              key={section.label}
              className="flex items-center justify-between rounded-xl border bg-card px-4 py-3"
            >
              <div>
                <p className="text-sm font-medium">{section.label}</p>
                <p className="text-xs text-muted-foreground">{section.description}</p>
              </div>
              <span
                className={
                  section.available
                    ? "text-sm font-medium text-green-600"
                    : "text-sm text-muted-foreground"
                }
              >
                {section.available ? "Available" : "—"}
              </span>
            </div>
          ))}
        </div>
      </div>

      {platform_kpis && (
        <div className="flex flex-col gap-4">
          <h2 className="text-base font-semibold">Platform overview</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KpiCard label="Total properties" value={platform_kpis.properties_total} />
            <KpiCard label="Live properties" value={platform_kpis.properties_live} />
            <KpiCard label="Suspended" value={platform_kpis.properties_suspended} />
            <KpiCard label="Pending moderation" value={platform_kpis.pending_moderation} />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <KpiCard label="Active guides" value={platform_kpis.active_guides} />
            <KpiCard label="Guest sessions (7d)" value={platform_kpis.guest_sessions_7d} />
            <KpiCard label="Guest sessions (30d)" value={platform_kpis.guest_sessions_30d} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border bg-card px-4 py-3">
              <p className="text-xs text-muted-foreground">Activation channel split</p>
              <ChannelSplitBar wifi={platform_kpis.channel_wifi} qr={platform_kpis.channel_qr} />
            </div>
            {platform_kpis.top_regions.length > 0 && (
              <div className="rounded-xl border bg-card px-4 py-3">
                <p className="mb-2 text-xs text-muted-foreground">Top regions (30d sessions)</p>
                <ol className="flex flex-col gap-1">
                  {platform_kpis.top_regions.map((r, i) => (
                    <li key={r.id} className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{i + 1}. {r.name}</span>
                      <span className="font-medium tabular-nums">{r.sessions_30d}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      )}

      {hasRegionalContext && (
        <div className="flex flex-col gap-4">
          <h2 className="text-base font-semibold">Regional overview</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KpiCard label="Properties" value={regional_kpis.properties_total} />
            <KpiCard label="Live" value={regional_kpis.properties_live} />
            <KpiCard label="Pending moderation" value={regional_kpis.pending_moderation} />
            <KpiCard label="Guest sessions (30d)" value={regional_kpis.guest_sessions_30d} />
          </div>
        </div>
      )}

      {hasRegionalContext && (
        <div className="flex flex-col gap-4">
          <h2 className="text-base font-semibold">Needs attention</h2>
          <NeedsAttentionBlock items={needs_attention} />
        </div>
      )}
    </div>
  )
}

Dashboard.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
